import { ENDPOINT_ASSESSOR } from "@constants/urls";
import { useAuthContext } from "@features/auth/contexts/AuthContext";
import i18n from "@helpers/lib/i18n";
import { isWeb } from "@helpers/platform";
import { useAppStore } from "@hooks/useAppStore";
import type { Passage } from "@memorang/types/src/session/content";
import type {
	BaseItemSchema,
	OriginalItemTypes,
} from "@memorang/types/src/session/new-session";
import { useToast } from "@memorang/ui/src/components/Toast";
import { runOnJS } from "react-native-reanimated";
import EventSource from "react-native-sse";
import { uploadRecording } from "../helpers/recording";
import useAIFeedbackStore from "./useAIFeedbackStore";
import useCurrentSession from "./useCurrentSession";
import useSessionStore from "./useSessionStore";

type GenerateExplanationParams = {
	currentItem: BaseItemSchema;
	itemType: OriginalItemTypes;
	onAiFeedbackProvided: () => void;
	audioUrl?: string | null;
	existingUserResponse?: string;
};

const MAX_RETRIES = 3;

const formatLanguageCode = (languageCode: string) => {
	return languageCode.replace(/-/g, "_").toLowerCase();
};

export const replaceBrackets = (str: string): string =>
	str.replace(/[{}[\]]/g, "");

const getStemAndTranscript = (item: BaseItemSchema) => {
	const stimulusContent = item.stimulus;

	let readingPassage: Passage | null = null;

	let listeningTranscript: string[] = [];

	if (stimulusContent) {
		readingPassage =
			stimulusContent.slides?.find((slide) => slide.title === "Read")
				?.passage ||
			stimulusContent.passage ||
			null;
	}

	if (item.content?.stimulus?.text) {
		readingPassage = {
			title: item.content?.stimulus?.title || "",
			paragraphs: [
				{
					number: 1,
					text: item.content?.stimulus?.text,
				},
			],
		};

		if (stimulusContent) {
			const slideTranscripts = stimulusContent.slides
				?.find((slide) => slide.title === "Listen")
				?.media?.map((media) => media.transcript);

			const mediaTranscripts = stimulusContent.media?.map(
				(media) => media.transcript || media.transcription,
			);

			listeningTranscript =
				slideTranscripts?.filter((t): t is string => !!t) ||
				mediaTranscripts?.filter((t): t is string => !!t) ||
				[];
		}
		if (item.content?.stimulus?.media) {
			listeningTranscript = item.content.stimulus.media
				.map((media) => media.transcript || media.transcription)
				.filter((t): t is string => !!t);
		}
		if (item.content?.media) {
			listeningTranscript = item.content.media
				.map((media) => media.transcript || media.transcription)
				.filter((t): t is string => !!t);
		}
	}

	return {
		readingPassage,
		listeningTranscript,
	};
};
/**
 * Formats the item for the AI feedback endpoint based on the item type
 * @param item
 */
export const formatItemForAI = (item: BaseItemSchema) => {
	const { stimulus, content, metadata, stimulusMetadata } = item;

	const baseFormat = {
		instruction: stimulus?.instruction?.text || item.content?.instruction?.text,
		stem: content?.stem?.text,
	};

	if (metadata?.type && !stimulusMetadata?.type) {
		switch (metadata?.type) {
			case "SPEAKING_RESPONSE":
			case "SPEAKING_READ_ALOUD":
			case "SPEAKING_EXTENDED_RESPONSE":
			case "LONG_TURN":
			case "SPEAKING_SHORT_RESPONSE":
			case "STORY_TELLING": {
				const { listeningTranscript } = getStemAndTranscript(item);

				return {
					...baseFormat,
					transcripts: listeningTranscript?.join("\n"),
				};
			}

			case "WRITING_EXTENDED_RESPONSE":
			case "WRITING_INTEGRATED_RESPONSE":
			case "WRITING_RESPONSE":
			case "WRITING_EMAIL": {
				const { readingPassage, listeningTranscript } =
					getStemAndTranscript(item);

				return {
					...baseFormat,
					passageTitle: stimulus?.passage?.title,
					readingPassageTitle: readingPassage?.title,
					readingPassageText: readingPassage?.paragraphs
						?.map((paragraph) => paragraph.text)
						.join("\n"),
					listeningTranscript: listeningTranscript?.join("\n\n"),
				};
			}

			default:
				return baseFormat;
		}
	}

	switch (stimulusMetadata?.type) {
		case "RLS_STIMULUS": {
			const { readingPassage, listeningTranscript } =
				getStemAndTranscript(item);

			return {
				...baseFormat,
				readingPassageTitle: readingPassage?.title,
				readingPassageText: readingPassage?.paragraphs
					?.map((paragraph) => paragraph.text)
					.join("\n"),
				listeningTranscript: listeningTranscript?.join("\n\n"),
			};
		}

		case "LS_STIMULUS": {
			const { listeningTranscript } = getStemAndTranscript(item);

			return {
				...baseFormat,
				passageTitle: stimulus?.passage?.title,
				listeningTranscript: listeningTranscript?.join("\n\n"),
			};
		}

		case "ACADEMIC_DISCUSSION_STIMULUS": {
			return {
				...baseFormat,
				className: stimulus?.className,
				professorName: stimulus?.professor?.name,
				questionTitle: stimulus?.professor?.questionTitle,
				questionBody: stimulus?.professor?.questionBody,
				discussion: content?.discussion
					?.map((d) => `${d.student.name}: ${d.post}`)
					.join("\n\n"),
			};
		}

		case "INTEGRATED_WRITING_STIMULUS": {
			const { readingPassage, listeningTranscript } =
				getStemAndTranscript(item);

			return {
				...baseFormat,
				passageTitle: stimulus?.passage?.title,
				readingPassageTitle: readingPassage?.title,
				readingPassageText: readingPassage?.paragraphs
					?.map((paragraph) => paragraph.text)
					.join("\n"),
				listeningTranscript: listeningTranscript?.join("\n\n"),
			};
		}

		case "INDEPENDENT_SPEAKING_STIMULUS": {
			const { listeningTranscript } = getStemAndTranscript(item);

			return {
				...baseFormat,
				transcripts: listeningTranscript?.join("\n"),
			};
		}
		default:
			return baseFormat;
	}
};

export const getParentStem = (currentItem: BaseItemSchema) => {
	const { stimulus, content, metadata } = currentItem;

	let parentStem = "";

	if (stimulus?.stem?.text) {
		parentStem = JSON.stringify(stimulus?.stem?.text);
	}

	if (stimulus?.passage?.paragraphs?.length) {
		parentStem = JSON.stringify(
			stimulus?.passage?.paragraphs
				?.map((paragraph) => paragraph.text)
				.join("\n"),
		);
	}

	if (content?.stimulus?.text) {
		parentStem = JSON.stringify(content?.stimulus?.text);
	}

	if (content?.discussion) {
		parentStem += `\n\nDiscussion: ${JSON.stringify(content?.discussion)}`;
	}

	if (content?.instruction) {
		parentStem += `\n\nInstruction: ${content?.instruction?.text}`;
	}

	if (metadata?.skill) {
		parentStem += `\n\nCurrent Skill: ${metadata?.skill}`;
	}

	return replaceBrackets(parentStem);
};

export const useAIFeedback = () => {
	const sessionId = useSessionStore((state) => state.id);
	const setStreaming = useAIFeedbackStore((store) => store.setStreaming);
	const setShowError = useAIFeedbackStore((store) => store.setShowError);
	const updateTranscription = useAIFeedbackStore(
		(store) => store.updateTranscription,
	);
	const userResponse = useAIFeedbackStore((store) => store.userResponse);
	const audioResponse = useAIFeedbackStore((store) => store.audioResponse);
	const updateRunDetails = useAIFeedbackStore(
		(store) => store.updateRunDetails,
	);
	const updateSubskillAssessments = useAIFeedbackStore(
		(store) => store.updateSubskillAssessments,
	);
	const updateScore = useAIFeedbackStore((store) => store.updateScore);
	const updateExplanation = useAIFeedbackStore(
		(store) => store.updateExplanation,
	);
	const updateAnswer = useAIFeedbackStore((store) => store.updateAnswer);
	const tenantId = useAppStore((store) => store.tenant.id);
	const { showToast } = useToast();
	const { viewerId: userId } = useAuthContext();
	const language = formatLanguageCode(i18n.language);
	const { currentItem } = useCurrentSession();

	const handleGenerateExplanation = async ({
		currentItem,
		itemType,
		audioUrl,
		existingUserResponse,
		onAiFeedbackProvided,
		// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
	}: GenerateExplanationParams) => {
		const isRecording = currentItem.response.type === "recording";
		const isWriting = currentItem.response.type === "writing";
		const isForm = currentItem.response.type === "form";

		let assessmentType = "";
		if (isRecording) {
			assessmentType = "langgraph-speaking";
		}

		if (isWriting) {
			assessmentType = "langgraph-writing";
		}
		if (isForm) {
			assessmentType = "langgraph-completion";
		}

		let stimulusTaskType: string =
			currentItem.stimulusMetadata?.type ?? itemType ?? "";

		if (
			stimulusTaskType &&
			["LS_STIMULUS", "RLS_STIMULUS"].includes(stimulusTaskType)
		) {
			stimulusTaskType = "INTEGRATED_SPEAKING_STIMULUS";
		}

		const formattedItem = formatItemForAI(currentItem);
		const answer = isRecording ? audioUrl : existingUserResponse;
		if (answer) {
			updateAnswer(currentItem.id!, answer);
		}

		const getMediaUrls = (): string[] => {
			const urls: string[] = [];

			if (currentItem.stimulus?.media) {
				urls.push(...currentItem.stimulus.media.map((media) => media.url));
			}

			if (currentItem.content?.media) {
				urls.push(...currentItem.content.media.map((media) => media.url));
			}

			if (currentItem.content?.stimulus?.media) {
				urls.push(
					...currentItem.content.stimulus.media.map((media) => media.url),
				);
			}

			return urls;
		};

		const body = {
			tenantId,
			userId,
			itemId: currentItem.id,
			options: {
				subtaskType: assessmentType,
				input: {
					...formattedItem,
					stimulusTaskType: stimulusTaskType,
					assessment_mode:
						itemType.includes("SPEAKING") || itemType.includes("STORY")
							? "Summative"
							: "Formative",
					imageUrls: {
						media: getMediaUrls(),
					},
					...(assessmentType === "langgraph-speaking" && audioUrl
						? { audioUrls: [audioUrl] }
						: {}),
					...(assessmentType === "langgraph-writing" &&
						existingUserResponse && {
							writing_response: String(existingUserResponse).trim(),
						}),
					...(assessmentType === "langgraph-completion" &&
						existingUserResponse && {
							completion_response: JSON.stringify(existingUserResponse),
						}),
					feedbackInputs: {
						language,
					},
				},
			},
		};

		setStreaming(true);
		setShowError(false);
		// onConfidenceProvided(50);

		const es = new EventSource(ENDPOINT_ASSESSOR, {
			method: "POST",
			headers: { "Content-Type": "text/event-stream" },
			body: JSON.stringify(body),
		});

		es.addEventListener("open", (_event) => {
			// console.info("Open SSE connection.1", event);
		});

		// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
		es.addEventListener("message", (event) => {
			if (!event.data) {
				return;
			}
			const message = JSON.parse(event.data);

			if (message.event === "close") {
				// console.info("closing");
				es.close();
				// return;
			}

			if (message.event === "runDetails") {
				// console.info("runDetails", message.data);
				updateRunDetails(currentItem.id!, message.data);
			}
			if (message.event === "toolCall") {
				const tool = message.data.tool;
				const response = message.data.response;

				if (tool === "transcriptionTool" && response) {
					const transcriptText = (response as { url: string; text: string }[])
						.map((r) => r.text)
						.join("\n");

					updateTranscription(currentItem.id!, transcriptText);
				}
			}

			if (message.event === "data") {
				const node = message.data.node;
				const response = message.data.response;
				if (node === "subskillAssessmentNode") {
					// console.info("subskillAssessmentNode", response);
					if (response.subskillAssessments) {
						updateSubskillAssessments(
							currentItem.id!,
							response.subskillAssessments,
						);
					}
				}
				if (node === "overallAssessmentNode") {
					if (response.score !== undefined) {
						updateScore(currentItem.id!, response.score);
					}
				}
				if (node === "feedbackNode") {
					// console.info("feedbackNode", response);
					if (response.feedback) {
						runOnJS(updateExplanation)(currentItem.id!, response.feedback);
					}
				}
			}
		});

		es.addEventListener("close", () => {
			onAiFeedbackProvided();
			setStreaming(false);
		});

		es.addEventListener("error", (event) => {
			console.error("Error from SSE.", event);

			const retries =
				useAIFeedbackStore.getState().aiResponse?.[currentItem.id!].retries ??
				0;

			if (retries < MAX_RETRIES) {
				const incrementRetries = useAIFeedbackStore.getState().incrementRetries;
				incrementRetries(currentItem.id!);
				return;
			}
			showToast({
				message: "Something went wrong, please restart the session",
			});
			es.close();
			// ? Uncomment to use mock data
			// 	updateRunDetails(currentItem.id!, mockRunDetails);
			// updateSubskillAssessments(
			// 	currentItem.id!,
			// 	mockSubskillAssessmentNode.subskillAssessments,
			// );
			// updateScore(currentItem.id!, mockOverallAssessmentNode.score);
			// const words = mockFeedbackNode.feedback.split(" ");
			// for (let i = 0; i < words.length; i += 2) {
			// 	const endIndex = Math.min(i + 2, words.length);
			// 	const partialFeedback = words.slice(0, endIndex).join(" ");
			// 	updateExplanation(currentItem.id!, partialFeedback);
			// 	await new Promise((resolve) => setTimeout(resolve, 25));
			// }
		});
	};

	const generateExplanation = async (onAiFeedbackProvided: () => void) => {
		const itemType = currentItem.metadata?.type!;
		const existingUserResponse = userResponse?.[currentItem.id!];
		const existingAudioResponse = audioResponse?.[currentItem.id!];

		if (existingUserResponse || existingAudioResponse) {
			setStreaming(true);
			let audioUrl: string | null = null;

			const fileName = `${userId}-${sessionId}-${currentItem.id}.${isWeb ? "webm" : "m4a"}`;

			let blob = existingUserResponse as Blob;

			if (currentItem.response.type === "recording") {
				if (isWeb) {
					blob = await fetch(existingUserResponse as string).then((res) =>
						res.blob(),
					);
				}
				audioUrl = await uploadRecording(blob, fileName);
			}

			handleGenerateExplanation({
				currentItem,
				itemType,
				onAiFeedbackProvided,
				audioUrl,
				existingUserResponse: existingUserResponse as string,
			});
		}
	};

	return {
		generateExplanation,
	};
};
